import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ValidationService } from './ValidationService';
import { ForecastTemplateColumns } from 'src/models/ForecastModels';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import { buildSystemRowLookup } from '../forecast-utils/RowResetUtils';
import { normalizePairForComparison } from '../forecast-utils/FieldComparisonUtils';
import { logger } from 'src/analytics/KatalLogger';

/**
 * Service for validating non-editable fields in forecast templates
 */
export class NonEditableFieldsValidationService extends ValidationService {
  /**
   * Validates that non-editable fields have not been modified.
   * @param forecastTemplateRelevantRowData The array of data objects from the file.
   * @param forecastTemplateColumns The forecast template columns configuration.
   * @param forecastTemplateCompleteData The array of complete data objects from the forecast template.
   * @param isBudgetLeaderOrAdmin Flag indicating if the current user is a budget leader or admin.
   * @returns Promise with validation status
   */
  public async validateNonEditableFields(
    forecastTemplateRelevantRowData: any[],
    forecastTemplateColumns: ForecastTemplateColumns,
    forecastTemplateCompleteData: any[],
    isBudgetLeaderOrAdmin: boolean
  ): Promise<ValidationStatusEntity> {
    const validationErrorDetails: ValidationErrorDetail[] = [];

    logger.info(`Validating Non editable Fields for ${forecastTemplateRelevantRowData.length} records`);

    const mandatoryFieldsWithoutBudgetOwner = forecastTemplateColumns.mandatoryFields.filter(
      (field) => field !== ForecastGridFixedFields.BudgetOwner.value
    );

    const mandatoryFields = forecastTemplateColumns.mandatoryFields;

    // Shared XptLineItemId -> systemRow lookup — same key strategy used by the preview grid's
    // Reset action and the auth validators, so any row that hits the "field X modified" branch
    // below resolves identically in the Reset code path.
    const completeDataMap = buildSystemRowLookup(forecastTemplateCompleteData);

    forecastTemplateRelevantRowData.forEach((row) => {
      const lineItemId = row[ForecastGridFixedFields.XptLineItemId.value];

      // If there is no Line Item Id, then it is new row, all fields are editable
      if (!lineItemId) {
        return;
      }

      // Determine which fields are non-editable based on row type and user role
      let nonEditableFieldsBasedOnRole: string[] = [];
      const isNewRow = row[ForecastGridFixedFields.IsNew.value];

      if (isNewRow) {
        // For new rows
        if (!isBudgetLeaderOrAdmin) {
          // Non-admin users can't edit budget owner on new rows
          nonEditableFieldsBasedOnRole = [ForecastGridFixedFields.BudgetOwner.value];
        } else {
          // Admins can edit all fields on new rows
          nonEditableFieldsBasedOnRole = [];
        }
      } else {
        // For existing rows
        if (!isBudgetLeaderOrAdmin) {
          // Non-admin users can't edit any mandatory fields on existing rows
          nonEditableFieldsBasedOnRole = mandatoryFields;
        } else {
          // Admins can edit budget owner but not other mandatory fields on existing rows
          nonEditableFieldsBasedOnRole = mandatoryFieldsWithoutBudgetOwner;
        }
      }

      // Check for modifications to non-editable fields. Emit `field` + `rowIndex` on each
      // detail so the preview grid paints the specific cell red and the prev/next-error nav
      // jumps to it — otherwise these show up only in the summary panel with no cell affordance.
      // `errorCategory: 'non-editable-field'` is what enables the preview's row-level
      // "Reset to system values" action for these rows — see RowResetUtils / UploadPreviewGrid.
      //
      // Normalize BOTH sides with the same rules FileChangeDetectionValidationService uses,
      // otherwise a clean round-trip through Excel + SheetJS + AG Grid produces spurious
      // "non-editable field" violations: `is_planned_in_usd` reads back as `false` where the
      // template holds `null`; corp segments read back as number `6060` where the template
      // holds string `"6060"`; forecast months pick up IEEE-754 trailing noise. Divergence
      // between this comparison and the file-change detection was flagged in CR review.
      const matchingRow = completeDataMap.get(`${lineItemId}`);
      if (matchingRow) {
        const rowNo = row[ForecastGridFixedFields.FileRowNumber.value] as number | undefined;
        const forecastMonthCols = new Set(forecastTemplateColumns.forecastMonthColumnsIds ?? []);
        nonEditableFieldsBasedOnRole.forEach((field) => {
          const { fileValue, systemValue } = normalizePairForComparison(row[field], matchingRow[field], field, { forecastMonthCols });
          if (fileValue !== systemValue) {
            validationErrorDetails.push({
              field,
              rowIndex: rowNo,
              errorCategory: 'non-editable-field',
              message: `${field} '${row[field]}' ${this.getLineItemIdMessagePart(
                row,
                ForecastGridFixedFields.XptLineItemId.value
              )} - Non-editable field`
            });
          }
        });
      }
    });

    // Log validation errors if any
    this.logValidationErrors('Non-editable Fields', validationErrorDetails);

    // Return appropriate validation status
    if (validationErrorDetails.length > 0) {
      return this.createErrorStatus(
        ForecastValidationMessages.NON_EDITABLE_FIELD_VALIDATION_FAILED,
        ForecastValidationMessages.NON_EDITABLE_FIELD_VALIDATION_DEFAULT,
        validationErrorDetails
      );
    }

    return this.createSuccessStatus(
      ForecastValidationMessages.NON_EDITABLE_FIELD_VALIDATION_SUCCESS,
      ForecastValidationMessages.NON_EDITABLE_FIELD_VALIDATION_DEFAULT
    );
  }
}

// Export singleton instance
export const nonEditableFieldsValidationService = new NonEditableFieldsValidationService();
